import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { OrgAdminApi } from '../../services/api/OrgAdminApi';
import CreateCourseModal from '../../components/courses/CreateCourseModal';
import './OrgAdminDashboard.css';

const ROLE_OPTIONS = [
  { value: 'org_member', label: 'Member' },
  { value: 'course_admin', label: 'Course Admin' },
  { value: 'org_admin', label: 'Org Admin' },
];

/**
 * Org-Admin Dashboard.
 * Route: /organization/:organizationId/admin
 * Server-side gated by @OrgAdminOnly on every endpoint.
 */
function OrgAdminDashboard() {
  const { organizationId } = useParams();
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [domains, setDomains] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [domainForm, setDomainForm] = useState({ domain: '', autoRole: 'org_member' });

  const handle = useCallback(async (loaderFn, setter) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await loaderFn();
      if (resp.status === 403) { setError('You do not have permission to administer this organization.'); return; }
      if (!resp.ok) { setError(`Request failed (${resp.status})`); return; }
      const body = await resp.json().catch(() => null);
      setter(Array.isArray(body) ? body : (body || []));
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    if (activeTab === 'members') handle(() => OrgAdminApi.getMembers(organizationId), setMembers);
    else if (activeTab === 'courses') handle(() => OrgAdminApi.getCourses(organizationId), setCourses);
    else if (activeTab === 'domains') handle(() => OrgAdminApi.getEmailDomains(organizationId), setDomains);
  }, [activeTab, organizationId, handle]);

  const onChangeRole = async (userId, orgRole) => {
    setError(null);
    const resp = await OrgAdminApi.updateMemberRole(organizationId, userId, orgRole);
    if (!resp.ok) {
      setError(`Failed to update role (${resp.status})`);
      return;
    }
    handle(() => OrgAdminApi.getMembers(organizationId), setMembers);
  };

  const onAddDomain = async (e) => {
    e.preventDefault();
    setError(null);
    if (!domainForm.domain.trim()) return;
    const resp = await OrgAdminApi.upsertEmailDomain(organizationId, {
      domain: domainForm.domain.trim().toLowerCase(),
      autoRole: domainForm.autoRole,
    });
    if (!resp.ok) {
      setError(`Failed to add domain (${resp.status})`);
      return;
    }
    setDomainForm({ domain: '', autoRole: 'org_member' });
    handle(() => OrgAdminApi.getEmailDomains(organizationId), setDomains);
  };

  const onDeleteDomain = async (id) => {
    setError(null);
    const resp = await OrgAdminApi.deleteEmailDomain(organizationId, id);
    if (!resp.ok) {
      setError(`Failed to delete domain (${resp.status})`);
      return;
    }
    handle(() => OrgAdminApi.getEmailDomains(organizationId), setDomains);
  };

  return (
    <div className="org-admin-dashboard">
      <header className="org-admin-header">
        <h1>Organization Admin</h1>
        <span className="org-admin-org-id">Org #{organizationId}</span>
      </header>

      <nav className="org-admin-tabs" role="tablist">
        {[
          ['members', 'Members'],
          ['courses', 'Courses'],
          ['domains', 'Email Domains'],
        ].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={activeTab === key}
            className={`org-admin-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >{label}</button>
        ))}
      </nav>

      <section className="org-admin-content">
        {loading && <div className="org-admin-loading">Loading…</div>}
        {error && <div className="org-admin-error" role="alert">{error}</div>}

        {!loading && activeTab === 'members' && (
          <MembersTable
            members={members}
            onChangeRole={onChangeRole}
            organizationId={organizationId}
            onMemberAdded={() => handle(() => OrgAdminApi.getMembers(organizationId), setMembers)}
          />
        )}

        {!loading && activeTab === 'courses' && (
          <CoursesTable
            courses={courses}
            organizationId={organizationId}
            onCourseCreated={() => handle(() => OrgAdminApi.getCourses(organizationId), setCourses)}
          />
        )}

        {!loading && activeTab === 'domains' && (
          <DomainsPanel
            domains={domains}
            form={domainForm}
            setForm={setDomainForm}
            onAdd={onAddDomain}
            onDelete={onDeleteDomain}
          />
        )}
      </section>
    </div>
  );
}

function MembersTable({ members, onChangeRole, organizationId, onMemberAdded }) {
  const [showForm, setShowForm] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [formError, setFormError] = React.useState(null);
  const [addForm, setAddForm] = React.useState({ email: '', orgRole: 'org_member' });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.email.trim()) return;
    setAdding(true);
    setFormError(null);
    try {
      const resp = await OrgAdminApi.addMember(organizationId, addForm.email.trim(), addForm.orgRole);
      if (resp.status === 404) { setFormError('No user found with that email.'); return; }
      if (resp.status === 409) { setFormError('User is already a member of this organization.'); return; }
      if (!resp.ok) { setFormError(`Failed to add member (${resp.status})`); return; }
      setAddForm({ email: '', orgRole: 'org_member' });
      setShowForm(false);
      onMemberAdded();
    } catch (err) {
      setFormError(err?.message || 'Network error');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="org-admin-tab"
          style={{ borderBottom: 'none', fontWeight: 600 }}
          onClick={() => setShowForm(f => !f)}
        >
          {showForm ? 'Cancel' : '+ Add Member'}
        </button>
      </div>

      {formError && <div className="org-admin-error" role="alert">{formError}</div>}

      {showForm && (
        <form className="org-admin-domain-form" onSubmit={handleAdd} style={{ marginBottom: 16 }}>
          <input
            type="email"
            placeholder="user@example.edu"
            value={addForm.email}
            onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
            required
          />
          <select value={addForm.orgRole} onChange={e => setAddForm(f => ({ ...f, orgRole: e.target.value }))}>
            {ROLE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button type="submit" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
        </form>
      )}

      {(!members || members.length === 0) ? (
        <div className="org-admin-empty">No members.</div>
      ) : (
        <table className="org-admin-table">
          <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {members.map(m => (
              <tr key={m.membershipId || m.userId}>
                <td>{m.userName || m.firstName || `User ${m.userId}`}</td>
                <td>{m.email || '—'}</td>
                <td>
                  <select
                    defaultValue={m.orgRole || 'org_member'}
                    onChange={e => onChangeRole(m.userId, e.target.value)}
                  >
                    {ROLE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </td>
                <td>{m.status || '—'}</td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CoursesTable({ courses, organizationId, onCourseCreated }) {
  const [showWizard, setShowWizard] = React.useState(false);

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="org-admin-tab"
          style={{ borderBottom: 'none', fontWeight: 600 }}
          onClick={() => setShowWizard(true)}
        >
          + Create Class
        </button>
      </div>

      <CreateCourseModal
        open={showWizard}
        orgId={organizationId}
        onClose={() => setShowWizard(false)}
        onCreated={() => { setShowWizard(false); onCourseCreated(); }}
      />

      {(!courses || courses.length === 0) ? (
        <div className="org-admin-empty">No org-owned courses.</div>
      ) : (
        <table className="org-admin-table">
          <thead><tr><th>Title</th><th>Owner</th><th>Ownership</th><th></th></tr></thead>
          <tbody>
            {courses.map(c => (
              <tr key={c.endeavorId || c.id}>
                <td>{c.name || c.title || `Course ${c.endeavorId || c.id}`}</td>
                <td>{c.ownerUserId || '—'}</td>
                <td>{c.ownershipType || 'org_owned'}</td>
                <td>
                  <Link to={`/course/${c.endeavorId || c.id}`} style={{ marginRight: 10 }}>View</Link>
                  <Link to={`/course/${c.endeavorId || c.id}/admin`}>Manage →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DomainsPanel({ domains, form, setForm, onAdd, onDelete }) {
  return (
    <div>
      <form className="org-admin-domain-form" onSubmit={onAdd}>
        <input
          type="text"
          placeholder="example.edu"
          value={form.domain}
          onChange={e => setForm({ ...form, domain: e.target.value })}
        />
        <select value={form.autoRole} onChange={e => setForm({ ...form, autoRole: e.target.value })}>
          <option value="org_member">Member</option>
          <option value="course_admin">Course Admin</option>
        </select>
        <button type="submit">Add domain</button>
      </form>
      {(!domains || domains.length === 0) ? (
        <div className="org-admin-empty">No email domains configured.</div>
      ) : (
        <table className="org-admin-table">
          <thead><tr><th>Domain</th><th>Auto-role</th><th></th></tr></thead>
          <tbody>
            {domains.map(d => (
              <tr key={d.id || d.emailDomainId}>
                <td>{d.domain}</td>
                <td>{d.autoRole}</td>
                <td>
                  <button className="org-admin-delete-btn" onClick={() => onDelete(d.id || d.emailDomainId)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default OrgAdminDashboard;
